import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { RequireShop, RequireAdmin } from "@/components/RouteGuards";
import { StorefrontShell } from "@/components/StoreLayout";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Wishlist from "@/pages/Wishlist";
import Orders from "@/pages/Orders";

import AdminLogin from "@/pages/AdminLogin";
import AdminLogout from "@/pages/admin/AdminLogout";
import Dashboard from "@/pages/admin/Dashboard";
import Inventory from "@/pages/admin/Inventory";
import MasterBills from "@/pages/admin/MasterBills";
import Rates from "@/pages/admin/Rates";
import DiamondRates from "@/pages/admin/DiamondRates";
import Customers from "@/pages/admin/Customers";
import AdminOrders from "@/pages/admin/AdminOrders";
import PartnerLedger from "@/pages/admin/PartnerLedger";
import Invoice from "@/pages/admin/Invoice";
import Settings from "@/pages/admin/Settings";
import Content from "@/pages/admin/Content";
import DailyTasks from "@/pages/admin/DailyTasks";
import Leads from "@/pages/admin/Leads";
import Users from "@/pages/admin/Users";
import WinBack from "@/pages/admin/WinBack";
import Suppliers from "@/pages/admin/Suppliers";
import Purchases from "@/pages/admin/Purchases";
import Karigars from "@/pages/admin/Karigars";
import JobWork from "@/pages/admin/JobWork";
import Reports from "@/pages/admin/Reports";
import DailyDaybook from "@/pages/admin/DailyDaybook";
import CustomBuildRequests from "@/pages/admin/CustomBuildRequests";
import PrintTagsPage from "@/pages/admin/PrintTagsPage";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
        <SiteContentProvider>
        <BrowserRouter>
          <Routes>
            {/* Shop storefront — gated: every route below (including the main
                store preview root "/") requires a logged-in Shop and redirects
                straight to /login otherwise. */}
            <Route path="/login" element={<Login />} />
            {/* Layout route: the shell (header/logo/footer + page crossfade)
                mounts ONCE here and persists across every storefront
                navigation, instead of being rebuilt inside each page. */}
            <Route element={<RequireShop><StorefrontShell /></RequireShop>}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/orders" element={<Orders />} />
            </Route>

            {/* Admin + CRM panels — gated: every route below requires a
                logged-in Admin/Manager/Staff session and redirects straight
                to /admin/login otherwise. */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/logout" element={<AdminLogout />} />
            <Route path="/admin" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
            <Route path="/admin/inventory" element={<RequireAdmin><Inventory /></RequireAdmin>} />
            <Route path="/admin/master-bills" element={<RequireAdmin><MasterBills /></RequireAdmin>} />
            <Route path="/admin/rates" element={<RequireAdmin><Rates /></RequireAdmin>} />
            <Route path="/admin/diamond-rates" element={<RequireAdmin><DiamondRates /></RequireAdmin>} />
            <Route path="/admin/suppliers" element={<RequireAdmin><Suppliers /></RequireAdmin>} />
            <Route path="/admin/purchases" element={<RequireAdmin><Purchases /></RequireAdmin>} />
            <Route path="/admin/karigars" element={<RequireAdmin><Karigars /></RequireAdmin>} />
            <Route path="/admin/job-work" element={<RequireAdmin><JobWork /></RequireAdmin>} />
            <Route path="/admin/reports" element={<RequireAdmin><Reports /></RequireAdmin>} />
            <Route path="/admin/daybook" element={<RequireAdmin><DailyDaybook /></RequireAdmin>} />
            <Route path="/admin/custom-requests" element={<RequireAdmin><CustomBuildRequests /></RequireAdmin>} />
            <Route path="/admin/customers" element={<RequireAdmin><Customers /></RequireAdmin>} />
            <Route path="/admin/settings" element={<RequireAdmin><Settings /></RequireAdmin>} />
            <Route path="/admin/content" element={<RequireAdmin><Content /></RequireAdmin>} />
            <Route path="/admin/users" element={<RequireAdmin><Users /></RequireAdmin>} />
            <Route path="/admin/print-tags" element={<RequireAdmin><PrintTagsPage /></RequireAdmin>} />

            {/* CRM panel (bifurcated from Admin panel) — same guard, same session */}
            <Route path="/crm" element={<RequireAdmin><Navigate to="/crm/leads" replace /></RequireAdmin>} />
            <Route path="/crm/leads" element={<RequireAdmin><Leads /></RequireAdmin>} />
            <Route path="/crm/daily-tasks" element={<RequireAdmin><DailyTasks /></RequireAdmin>} />
            <Route path="/crm/winback" element={<RequireAdmin><WinBack /></RequireAdmin>} />
            <Route path="/crm/orders" element={<RequireAdmin><AdminOrders mode="orders" /></RequireAdmin>} />
            <Route path="/crm/invoices" element={<RequireAdmin><AdminOrders mode="invoices" /></RequireAdmin>} />
            <Route path="/crm/partner-ledger" element={<RequireAdmin><PartnerLedger /></RequireAdmin>} />
            <Route path="/crm/invoice/:orderId" element={<RequireAdmin><Invoice /></RequireAdmin>} />

            {/* Unknown URLs fall back to the (guarded) home route rather than rendering blank */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </SiteContentProvider>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;

